// Static content for the portfolio
import {
  LineChart,
  Wallet,
  Building2,
  BrainCircuit,
  ShieldCheck,
  ClipboardCheck,
} from "lucide-react";

export const PROFILE = {
  name: "Natalia Halipchak",
  title: "Controller · Corporate Finance & Accounting",
  tagline:
    "I transform financial data into clear insights that support better strategic decisions.",
  bio: "Results-driven Controller with strong corporate finance expertise, actively pursuing both the CPA (PREP) and CMA designations. I manage month-end close, financial statements, budgets, forecasts, and internal controls — turning complex financial data into actionable insights for leadership.",
  heroPortrait:
    "https://images.unsplash.com/photo-1774425316479-4de6166e7408?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmaW5hbmNpYWwlMjBkaXN0cmljdCUyMGFyY2hpdGVjdHVyZSUyMGFic3RyYWN0fGVufDB8fHx8MTc4MzkxNzAyNXww&ixlib=rb-4.1.0&q=85",
  email: "hello@nataliahalipchak.com",
  phone: "+1 226 503 0686",
  location: "Winnipeg, Manitoba · Canada",
  linkedin: "https://www.linkedin.com/in/natalia-halipchak",
  instagram: "https://www.instagram.com/natalia.halipchak",
  resumeUrl:
    "https://customer-assets.emergentagent.com/job_portfolio-showcase-3177/artifacts/0mk0n3ox_Natalia_Halipchak_Resume2026.pdf",
};

export const STATS = [
  { value: "CPA", label: "PREP · In Progress" },
  { value: "CMA", label: "Certification · In Progress" },
  { value: "Full-Cycle", label: "Corporate Finance & Reporting" },
];

export const PROJECTS = [
  {
    id: "financial-performance-dashboard",
    title: "Financial Performance Dashboard",
    blurb:
      "An interactive dashboard tracking revenue, expenses, profitability, cash flow, and key business performance indicators.",
    tags: ["Power BI", "KPIs", "Analytics"],
    icon: LineChart,
    image:
      "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBkYXRhJTIwYW5hbHl0aWNzJTIwbW9kZXJuJTIwbWluaW1hbHxlbnwwfHx8fDE3ODM5MTU5NDZ8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7",
    role: "Financial Analyst · Controller",
    timeframe: "2025",
    overview:
      "Leadership was making decisions from static month-end PDFs that arrived days after close. I built a single, always-current view of the numbers that actually drive the business.",
    challenge:
      "Reporting was fragmented across spreadsheets, arrived late, and rarely explained the 'why' behind a movement. Executives couldn't see profitability or cash trends without asking finance to pull another report.",
    approach: [
      "Mapped the KPIs leadership genuinely uses to steer the business — revenue, gross margin, operating expenses, profitability, and cash position.",
      "Connected source data from the accounting system into a Power BI model with a clean, reconciled data layer.",
      "Designed drill-downs so each headline number can be traced to its underlying drivers by department and period.",
      "Automated the refresh so the dashboard is current the moment the books are closed.",
    ],
    outcomes: [
      "Leadership self-serves answers instead of waiting on ad-hoc report requests.",
      "Variance conversations now start from a shared, reconciled source of truth.",
      "Month-end reporting shifted from backward-looking scorekeeping to forward-looking decisions.",
    ],
  },
  {
    id: "13-week-cash-flow-forecast",
    title: "13-Week Cash-Flow Forecast",
    blurb:
      "A rolling cash-flow model designed to identify funding gaps, manage liquidity, and support short-term financial planning.",
    tags: ["Liquidity", "Forecasting", "Excel"],
    icon: Wallet,
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBkYXRhJTIwYW5hbHl0aWNzJTIwbW9kZXJuJTIwbWluaW1hbHxlbnwwfHx8fDE3ODM5MTU5NDZ8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
    role: "Controller",
    timeframe: "Ongoing",
    overview:
      "Profit is an opinion; cash is a fact. I built a rolling 13-week forecast that gives leadership an honest, near-term view of liquidity — long enough to spot trouble, short enough to stay accurate.",
    challenge:
      "The business could be profitable on paper yet still feel tight on cash. There was no reliable way to anticipate the timing of receipts and payments or to see a funding gap before it became urgent.",
    approach: [
      "Anchored the model to the actual bank balance rather than accrual-based P&L figures.",
      "Modeled receipts by customer and cohort so a single large client paying late is visible immediately.",
      "Separated committed outflows (payroll, rent, debt) from discretionary spend to expose real levers in a crunch.",
      "Built a weekly roll-forward routine so the forecast compounds in accuracy over time.",
    ],
    outcomes: [
      "Funding gaps surface weeks in advance instead of at the last minute.",
      "Leadership can time investments, hiring, and vendor negotiations from a position of clarity.",
      "Liquidity planning became a proactive, routine conversation.",
    ],
  },
  {
    id: "budget-vs-actual-analysis",
    title: "Budget vs. Actual Analysis",
    blurb:
      "A financial reporting tool that compares actual results against budgets, highlights variances, and explains major performance drivers.",
    tags: ["Variance", "Reporting", "Budgeting"],
    icon: Building2,
    image:
      "https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBwcmVjaXNpb258ZW58MHx8fHwxNzgzOTE1OTQ1fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
    role: "Controller · Staff Accountant",
    timeframe: "Monthly cycle",
    overview:
      "A budget only earns its keep when it changes behaviour. I built a variance analysis process that not only flags where actuals diverge from plan, but explains why — and what to do about it.",
    challenge:
      "Variances were reported as raw numbers with little context. Managers saw that they were over or under budget, but not the driver — price, volume, timing, or a one-off — so the report rarely led to action.",
    approach: [
      "Structured budgets bottom-up so every line has a clear owner and assumption.",
      "Built an automated actual-vs-budget comparison with materiality thresholds to surface only what matters.",
      "Attached a driver and a short narrative to each significant variance.",
      "Standardized the monthly package so it reads consistently for non-finance stakeholders.",
    ],
    outcomes: [
      "Reviews focus on the two or three variances that actually move the business.",
      "Managers receive explanations, not just numbers — leading to real corrective action.",
      "Budget accuracy improved cycle over cycle as assumptions were refined.",
    ],
  },
  {
    id: "cfo-ai-playbook",
    title: "CFO AI Playbook",
    blurb:
      "A practical collection of AI-powered workflows, prompts, and frameworks for financial analysis, reporting, forecasting, and executive decision-making.",
    tags: ["AI", "Frameworks", "Automation"],
    icon: BrainCircuit,
    image:
      "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwYWJzdHJhY3QlMjBtaW5pbWFsfGVufDB8fHx8MTc4MzkxNTk0NXww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-7",
    role: "Author",
    timeframe: "2025 — Present",
    overview:
      "There's a lot of noise about AI in finance and very little signal. The CFO AI Playbook is my practical, tested collection of workflows, prompts, and frameworks for the finance work that actually benefits from AI today.",
    challenge:
      "Finance teams are curious about AI but unsure where it adds value without compromising accuracy, traceability, or audit-readiness — and where it simply doesn't belong.",
    approach: [
      "Curated high-value use cases: variance-commentary drafting, reconciliation triage, and scenario prompting.",
      "Built a reusable prompt library so 'what-if' analysis takes minutes instead of a model rebuild.",
      "Codified guardrails — never cite a number a model can't trace, always keep a human in the loop for anything auditor- or board-facing.",
      "Documented each workflow so a finance team can adopt it without deep technical setup.",
    ],
    outcomes: [
      "Routine drafting and triage tasks are compressed, freeing time for real analysis.",
      "AI is applied where it's safe and valuable — and deliberately avoided where it isn't.",
      "A repeatable framework any finance function can adopt with confidence.",
    ],
  },
];

export const SKILLS = [
  "Financial Reporting",
  "Month-End Close",
  "Budgeting & Forecasting",
  "Cash-Flow Management",
  "Variance Analysis",
  "Internal Controls",
  "Taxation & CRA Compliance",
  "Audit Coordination",
  "Payroll Administration",
  "ASPE / PSAS / GAAP",
  "Power BI & Dynamics 365",
  "Process Automation",
];

export const TOOLS = [
  "Power BI",
  "Dynamics 365",
  "QuickBooks Online",
  "Sage 50",
  "Advanced Excel",
  "Cloud ERP",
  "Aileron",
];

export const EXPERTISE = [
  {
    icon: LineChart,
    title: "Financial Reporting",
    text: "Accurate, audit-ready statements and executive dashboards with clear variance analysis.",
  },
  {
    icon: Wallet,
    title: "Cash-Flow & Forecasting",
    text: "Rolling forecasts, budgets, and liquidity models that support leadership decisions.",
  },
  {
    icon: ShieldCheck,
    title: "Controls & Compliance",
    text: "Accounting policies, internal controls, payroll, and 100% CRA compliance across entities.",
  },
  {
    icon: BrainCircuit,
    title: "Systems & Automation",
    text: "Reporting automation, ERP optimization, and AI workflows that make finance faster.",
  },
];

export const EXPERIENCE = [
  {
    role: "Controller",
    company: "Hilltop Partner Network",
    period: "Dec 2025 — Present",
    points: [
      "Own month-end close, reconciliations, and executive financial reporting with clear variance analysis.",
      "Lead budgeting, rolling forecasts, and cash-flow management to support leadership decisions.",
      "Establish accounting policies, internal controls, and reporting automation for accurate, auditable financials.",
    ],
  },
  {
    role: "Staff Accountant",
    company: "Lambert Cook CPA",
    period: "Jul 2024 — Dec 2025",
    points: [
      "Prepared financial statements and working papers for corporate and not-for-profit clients under ASPE / PSAS.",
      "Handled complex tax engagements and full T2 corporate filings for multi-entity groups.",
      "Coordinated audit and review files, supporting partners during assurance engagements.",
    ],
  },
  {
    role: "Financial Analyst",
    company: "Hilltop Partner Network",
    period: "Sep 2025 — Dec 2025",
    points: [
      "Led full-cycle accounting and financial analysis including KPI reporting and budgets.",
      "Built monthly Power BI dashboards to evaluate profitability and project performance.",
      "Partnered with leadership on process improvement, automation, and data accuracy.",
    ],
  },
  {
    role: "Payroll & Finance Coordinator",
    company: "Vanier Children's Mental Wellness",
    period: "Dec 2023 — May 2024",
    points: [
      "Implemented a cloud-based payroll system, reducing errors and improving efficiency for 100+ employees.",
      "Processed bi-weekly payroll, T4s, WSIB reports, and CRA remittances with 100% compliance.",
      "Supported leadership with customized financial reports and variance analysis.",
    ],
  },
  {
    role: "Financial & Administrative Coordinator",
    company: "Great London International Airport",
    period: "May 2022 — Dec 2023",
    points: [
      "Managed payroll operations, T4s, WSIB filings, and reconciliations across multiple revenue streams.",
      "Supported digital transformation via Aileron implementation, improving reporting efficiency by 15%.",
      "Prepared monthly financials and supported internal and external audit processes.",
    ],
  },
];

export const CREDENTIALS = [
  { title: "CPA PREP", org: "CPA Ontario", meta: "In Progress" },
  { title: "CMA Certification", org: "Certified Management Accountant", meta: "In Progress" },
  { title: "Diploma, Accounting", org: "McMaster University", meta: "Expected 2026" },
  { title: "Diploma, Accounting & Auditing", org: "Ivano-Frankivsk NTU of Oil and Gas", meta: "2014" },
  { title: "Master's Degree, Information Technologies & Documentation", org: "Ivano-Frankivsk NTU of Oil and Gas", meta: "2011" },
];

export const VOLUNTEER = [
  { role: "Board Member & Fundraising Committee", org: "Horizon Magazine, Winnipeg", period: "Present" },
  { role: "Finance & Audit Committee Member", org: "Thames Valley Family Health Team", period: "2024 — Present" },
];
